import { Company } from '@dieterApi/company/useCompanyQuery';
import { UserValue } from '@dieterApi/persistentState/useUserValuesQuery';
import { User, UserQuestionnaireApplicationTopic, UserTopicApplications } from '@dieterApi/user/useUserQuery';
import { PartnerData } from './PartnerListEntry';
import { ContractDirection, contractTypeMapping } from './typesAndEnums';

// Performance logging utility (duplicated to avoid circular deps)
const perfLog = {
  start: (label: string) => {
    if (process.env.NODE_ENV === 'development') {
      performance.mark(`${label}-start`);
    }
  },
  end: (label: string) => {
    if (process.env.NODE_ENV === 'development') {
      performance.mark(`${label}-end`);
      performance.measure(label, `${label}-start`, `${label}-end`);
      const measure = performance.getEntriesByName(label)[0];
      if (measure) {
        console.log(`  ⏱️ [${label}]: ${measure.duration.toFixed(2)}ms`);
      }
    }
  },
};

export const extractPartnerData = (
  user: User,
  userValues: UserValue[],
  company: Company,
  topics: UserTopicApplications[],
  questionnaires: UserQuestionnaireApplicationTopic[] | undefined
): PartnerData[] => {
  perfLog.start('extractPartnerData-total');

  // Build lookup maps for O(1) access instead of repeated O(n) finds
  perfLog.start('extractPartnerData-buildMaps');
  const userValuesByKey = new Map<string, Map<string, string>>();

  userValues.forEach((uv) => {
    if (!userValuesByKey.has(uv.key)) {
      userValuesByKey.set(uv.key, new Map());
    }
    userValuesByKey.get(uv.key)!.set(uv.index, uv.value);
  });

  const allFiles = topics.flatMap((topic) => topic.files);
  const filesById = new Map(allFiles.map((file) => [file.id, file]));
  const signatureRequestsById = new Map((company.signatureRequests || []).map((sr) => [sr.id, sr]));
  const questionnairesByIndex = new Map((questionnaires || []).map((q) => [q.userValueIndex, q]));

  perfLog.end('extractPartnerData-buildMaps');

  perfLog.start('extractPartnerData-loop');
  const partnerData: PartnerData[] = [];
  let processedCount = 0;

  userValues.forEach((userValue) => {
    if (userValue.key === 'DATA_PROCESSOR_NAME') {
      processedCount++;
      const idx = userValue.index;
      const partnerName = userValue.value;

      // Use map lookups instead of repeated finds
      const address = userValuesByKey.get('DATA_PROCESSOR_ADDRESS')?.get(idx);
      const contractLocked = userValuesByKey.get('DATA_PROCESSOR_CONTRACT_LOCKED')?.get(idx) === 'true';
      const contractSigned = userValuesByKey.get('DATA_PROCESSOR_CONTRACT_SIGNED')?.get(idx) === 'true';
      const contractType = userValuesByKey.get('DATA_PROCESSOR_TYPE')?.get(idx);
      const contractFileId = userValuesByKey.get('DATA_PROCESSOR_CONTRACTFILE')?.get(idx);
      const direction = userValuesByKey.get('DATA_PROCESSOR_DIRECTION')?.get(idx) as ContractDirection | undefined;
      const isSubContractor = userValuesByKey.get('DATA_PROCESSOR_ISSUBCONTRACTOR')?.get(idx) === 'true';
      const tomsFileId = userValuesByKey.get('DATA_PROCESSOR_TOMS')?.get(idx);
      const signatureRequestId = userValuesByKey.get('DATA_PROCESSOR_SIGNATURE_REQUEST')?.get(idx);

      // Use map lookups for files and other lookups
      const signatureRequest = signatureRequestId ? signatureRequestsById.get(signatureRequestId) : undefined;
      const signedContractFileId = userValuesByKey.get('DATA_PROCESSOR_SIGNED_CONTRACT')?.get(idx);
      const signedContractFile = signedContractFileId ? filesById.get(signedContractFileId) : undefined;
      const contractFile = contractFileId ? filesById.get(contractFileId) : undefined;
      const toms = tomsFileId ? filesById.get(tomsFileId) : undefined;
      const contract = questionnairesByIndex.get(idx);

      partnerData.push({
        partnerName,
        address,
        contractType,
        contractLocked,
        contractSigned,
        contractFile,
        signedContractFile,
        signatureRequest,
        contract,
        direction,
        index: userValue.index,
        isSubContractor,
        toms,
      });
    }
  });
  perfLog.end('extractPartnerData-loop');
  console.log(`  📊 Processed ${processedCount} partners`);

  perfLog.start('extractPartnerData-filter');
  // only return partners where contractType is filled and which has not None
  const filtered = partnerData.filter(
    (pd) => pd.contractType && Object.keys(contractTypeMapping).includes(pd.contractType)
  );
  perfLog.end('extractPartnerData-filter');
  console.log(`  ✅ Filtered to ${filtered.length} valid partners`);

  perfLog.end('extractPartnerData-total');
  return filtered;
};
